import { useState } from "react";
import { MapPin } from "lucide-react";
import { adminApi } from "../../../lib/adminApi";
import { useConfig } from "../../../context/BrandContext";
import { Card, TextInput, TextArea, SelectInput, CheckInput, Btn, run, emailToast, fmtDateTime } from "../AdminUI";
import { StatusBadge } from "../../track/StatusBadge";

const GATED = ["Carrier Booked", "Dispatched", "In Transit", "Delivered"];
const EMPTY = { note: "", location: "", window_start: "", window_end: "", window_note: "", notify: true };

const PostForm = ({ sh, apply }) => {
  const { statuses } = useConfig();
  const closed = ["Cancelled", "Delivered"].includes(sh.status);
  const [status, setStatus] = useState(sh.status === "Cancelled" ? "Registered" : sh.status);
  const [f, setF] = useState(() => ({ ...EMPTY, location: sh.current_location || "", window_start: sh.delivery_window?.start || "", window_end: sh.delivery_window?.end || "", window_note: sh.delivery_window?.note || "" }));
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));
  const options = statuses.map((s) => ({
    value: s,
    label: `${s}${GATED.includes(s) && !sh.payment_gate_ok ? " (needs payment / COD)" : ""}${s === "Payment Confirmed" && !sh.payment ? " (use Payment panel)" : ""}${s === "Invoice Sent" && !sh.invoice ? " (create invoice first)" : ""}`,
  }));
  const post = async () => {
    const body = { status, note: f.note, location: f.location || null, window_start: f.window_start || null, window_end: f.window_end || null, window_note: f.window_note || null, notify: f.notify };
    const r = await run(() => adminApi.post(`/admin/shipments/${sh.tracking_number}/milestones`, body), `Update posted: ${status}`);
    if (r) { apply(r.data); emailToast(r.data.email); setF((p) => ({ ...p, note: "" })); }
  };
  return (
    <Card title="Post update" testid="milestone-form">
      {closed ? <p className="text-sm text-slate-600" data-testid="milestone-closed">This shipment is {sh.status}. No further updates.</p> : (
        <div className="grid sm:grid-cols-2 gap-4">
          <SelectInput label="Status" value={status} onChange={setStatus} options={options} testid="milestone-status-select" />
          <TextInput label="Location (City, ST)" value={f.location} onChange={set("location")} testid="milestone-location" placeholder="Salt Lake City, UT" />
          <TextArea label="Public note (shown on tracking page)" value={f.note} onChange={set("note")} testid="milestone-note" className="sm:col-span-2" />
          <TextInput label="Delivery window start" type="date" value={f.window_start} onChange={set("window_start")} testid="milestone-window-start" />
          <TextInput label="Delivery window end / ETA" type="date" value={f.window_end} onChange={set("window_end")} testid="milestone-window-end" />
          <TextInput label="Window note (optional)" value={f.window_note} onChange={set("window_note")} testid="milestone-window-note" className="sm:col-span-2" placeholder="Driver will call 2 hours before arrival" />
          <CheckInput label="Notify customer by email" checked={f.notify} onChange={set("notify")} testid="milestone-notify" />
          <div className="flex justify-end"><Btn onClick={post} data-testid="milestone-post-button">Post update</Btn></div>
        </div>
      )}
    </Card>
  );
};

const CancelCard = ({ sh, apply }) => {
  const [reason, setReason] = useState("");
  const [publicNote, setPublicNote] = useState("This shipment has been cancelled. Contact us with any questions.");
  const [notify, setNotify] = useState(true);
  const [armed, setArmed] = useState(false);
  if (["Cancelled", "Delivered"].includes(sh.status)) {
    return sh.cancel_reason ? <Card title="Cancellation"><p className="text-sm text-slate-700" data-testid="cancel-reason">Reason (internal): {sh.cancel_reason}</p></Card> : null;
  }
  const cancel = async () => {
    const r = await run(() => adminApi.post(`/admin/shipments/${sh.tracking_number}/cancel`, { reason, public_note: publicNote, notify }), "Shipment cancelled");
    if (r) { apply(r.data); emailToast(r.data.email); }
    setArmed(false);
  };
  return (
    <Card title="Cancel shipment" testid="cancel-card">
      <div className="grid sm:grid-cols-2 gap-4">
        <TextArea label="Reason (internal)" value={reason} onChange={setReason} testid="cancel-reason-input" />
        <TextArea label="Public note" value={publicNote} onChange={setPublicNote} testid="cancel-public-note" />
        <CheckInput label="Notify customer by email" checked={notify} onChange={setNotify} testid="cancel-notify" />
        <div className="flex justify-end gap-2">
          {armed && <Btn variant="ghost" onClick={() => setArmed(false)} data-testid="cancel-abort">Keep shipment</Btn>}
          <Btn variant="danger" disabled={reason.trim().length < 3} onClick={armed ? cancel : () => setArmed(true)} data-testid={armed ? "cancel-confirm-button" : "cancel-shipment-button"}>
            {armed ? "Confirm cancellation" : "Cancel shipment"}
          </Btn>
        </div>
      </div>
    </Card>
  );
};

export const MilestonesTab = ({ sh, apply }) => (
  <div className="space-y-6" data-testid="milestones-tab">
    <PostForm key={sh.updated_at} sh={sh} apply={apply} />
    <Card title="Milestone history">
      <ol className="space-y-4" data-testid="admin-milestone-history">
        {sh.milestones.map((m, i) => (
          <li key={m.id} className="flex gap-4 pb-4 border-b border-slate-100 last:border-0" data-testid={`admin-milestone-${i}`}>
            <div className="w-40 shrink-0 text-xs text-slate-500">{fmtDateTime(m.timestamp)}</div>
            <div className="flex-1">
              <StatusBadge status={m.status} testid={`admin-milestone-status-${i}`} />
              {m.note && <p className="mt-2 text-sm text-slate-700">{m.note}</p>}
              {m.location && <p className="mt-1 text-xs text-amber-800 flex items-center gap-1"><MapPin className="w-3 h-3" />{m.location}</p>}
            </div>
          </li>
        ))}
      </ol>
    </Card>
    <CancelCard sh={sh} apply={apply} />
  </div>
);
